'use client';
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

export type Language = 'pt-BR' | 'en' | 'es';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType>({
  language: 'pt-BR',
  setLanguage: () => {},
  t: (key) => key,
});

type Translations = Record<string, string>;
const translationCache: Partial<Record<Language, Translations>> = {};

async function loadTranslations(lang: Language): Promise<Translations> {
  if (translationCache[lang]) return translationCache[lang]!;
  try {
    const mod = await import(`@/locales/${lang}.json`);
    translationCache[lang] = mod.default as Translations;
    return translationCache[lang]!;
  } catch {
    return {};
  }
}

const PREFS_KEY = 'betvault_prefs';

function getStoredPrefs(): { language?: Language } {
  if (typeof window === 'undefined') return {};
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function savePrefs(prefs: { language: Language }) {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
  } catch {
    return;
  }
}

function getBrowserLanguage(): Language {
  if (typeof window === 'undefined') return 'pt-BR';
  const browserLang = navigator.language || 'pt-BR';
  if (browserLang.startsWith('pt')) return 'pt-BR';
  if (browserLang.startsWith('es')) return 'es';
  return 'en';
}

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguageState] = useState<Language>('pt-BR');
  const [translations, setTranslations] = useState<Translations>({});

  // Load stored preference on mount, fallback to browser language
  useEffect(() => {
    const prefs = getStoredPrefs();
    const lang: Language = prefs.language || getBrowserLanguage();
    setLanguageState(lang);
    loadTranslations(lang).then(setTranslations);
  }, []);

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
    savePrefs({ language: lang });
    loadTranslations(lang).then(setTranslations);
  }, []);

  const t = useCallback(
    (key: string): string => {
      return translations[key] ?? key;
    },
    [translations]
  );

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  return useContext(LanguageContext);
}
